import clsx from "clsx";
import css from "./Update.module.css";

import { Config } from "@planetscale/database";
import { useState } from "react";
import { Link, redirect, useNavigate } from "react-router";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { Heading3, Heading4 } from "~/components/ui/Heading";
import { PageTitle } from "~/components/ui/PageTitle";
import { GameVer, useRealm, useT, useTurl } from "~/util/Lang";
import {
  $API,
  $LOADER,
  $PAGE,
  $SERVER,
  callApi,
  OurLoaderArgs,
} from "~/util/Loaders";
import { requireSess } from "~/auth.server";
import { pg } from "~/pg.server";
import { and, eq, isNull } from "drizzle-orm";
import slug from "slug";
import { z } from "zod";
import { wikiPage } from "~/util/wiki";
import { PageEditor } from "~/components/site/PageEditor";
import { logPageView } from "~/util/stats";

const TITLE_PAGE = {
  en: "Updating the Wiki Page",
  ru: "Обновление Wiki Страницы",
};

const Params = z.object({ pageId: z.string() });

async function makeData(args: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(args);
  //
  const sess = requireSess(args.req.sess);
  if (!sess) {
    return redirect("/login");
  }
  const params = Params.parse(args.params);
  const page = await pg.db
    .select({
      id: pg.mdPages.id,
      slug: pg.mdPages.wikiSlug,
      game: pg.mdPages.game,
      createdAt: pg.mdPages.createdAt,
      revision: {
        id: pg.mdRevisions.id,
        titleEn: pg.mdRevisions.titleEn,
        contentEn: pg.mdRevisions.contentEn,
        titleRu: pg.mdRevisions.titleRu,
        contentRu: pg.mdRevisions.contentRu,
        createdAt: pg.mdRevisions.createdAt,
      },
    })
    .from(pg.mdPages)
    .where(
      and(
        eq(pg.mdPages.id, parseInt(params.pageId)),
        isNull(pg.mdPages.npcName),
        isNull(pg.mdPages.itemName),
        isNull(pg.mdPages.locName),
        isNull(pg.mdPages.questId),
        isNull(pg.mdPages.skillId),
        isNull(pg.mdPages.draftId),
      ),
    )
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))

    .then((res) => res.at(0));

  if (!page) {
    return redirect("/wiki");
  }

  return { page, authUser: args.req.sess?.user };
}

$PAGE({
  path: "wiki/:pageId/edit",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => {
    const { t } = cx;
    return { title: `${t(TITLE_PAGE)} - ${t(wikiPage)} - L2Hub.info` };
  },
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const pageTitle = t(TITLE_PAGE);
    const { page, authUser } = data;

    return (
      <div>
        <div className="content">
          <Breadcrumb
            items={[
              {
                title: t({ en: "Wiki Pages", ru: "Страницы" }),
                url: turl("/wiki"),
              },
              { title: pageTitle, url: turl(`/wiki/create`) },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>

          <PageEditor
            authUser={authUser}
            target={{ _: "wiki", pageId: page.id }}
            page={page}
          />
        </div>
      </div>
    );
  },
});

function slugGenerate(id: number, slugText: string | null) {
  return slugText ? slug(id + " " + slugText) : null;
}
